<script setup>
import { AbcBadge } from "@ordbogen-npm/abc-dsk";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRouter } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { getErrorMessage, withReplicationDelay } from "@/common/api/utils";
import VocabService from "@/common/api/vocab.service";
import NotificationService from "@/common/notification.service";
import { WordListType } from "@/common/types/vocab";
import { useAuthStore } from "@/stores";

import WordListContainerExamples from "./WordListContainerExamples.vue";

const props = defineProps({
  list: {
    type: Object,
    required: true
  },
  canStartQuiz: {
    type: Boolean,
    default: false
  }
});

// General framework stuff
const { t } = useI18n();

const router = useRouter();
const authStore = useAuthStore();
const { userIsTeacher } = storeToRefs(authStore);

const joinClassQuiz = computed(
  () => props.canStartQuiz && props.list.currentClassQuiz && !userIsTeacher.value
);
const openClassQuiz = computed(
  () => props.canStartQuiz && props.list.currentClassQuiz && userIsTeacher.value
);
const startClassQuiz = computed(
  () => props.canStartQuiz && !props.list.currentClassQuiz && userIsTeacher.value
);

const isCreatingGame = ref(false);

const shareCode = computed(() => props.list.shareCode);
const quizCode = computed(() => props.list.currentClassQuiz?.code);

const lemmas = computed(() => props.list.words.map((w) => w.lemma));

const isNew = computed(() => {
  const listDate = new Date(props.list.createdAt);
  const newDateLimit = new Date(Date.now() - 1209600000); // Number is 14 days in ms

  return listDate > newDateLimit && props.list.type === WordListType.Public;
});

const stopDoubleNavigation = () => {
  // Just a no-op to avoid outer router-link hijacking click event from inner router-links
};

const createClassQuiz = async () => {
  // Disable double creation, while still hi-jacking click.
  // So another click event, won't trigger click on the outer link.
  if (isCreatingGame.value) {
    return;
  }

  isCreatingGame.value = true;

  try {
    const { data } = await withReplicationDelay(() =>
      VocabService.createClassQuiz(shareCode.value)
    );

    router.push({
      name: "vocab.classQuiz",
      params: { shareCode: shareCode.value, quizCode: data.data.code }
    });
  } catch (error) {
    if (getErrorMessage(error) === "game_type_not_available") {
      NotificationService.error(t("api.game_type_not_available"));
    } else if (getErrorMessage(error) === "invalid_game_type") {
      NotificationService.error(t("api.invalid_game_type"));
    }
  } finally {
    isCreatingGame.value = false;
  }
};
</script>

<template>
  <RouterLink
    v-slot="{ href, navigate }"
    :to="{ name: 'vocab.list', params: { shareCode } }"
    custom
  >
    <div
      role="link"
      :href="href"
      tabindex="0"
      class="word-list-container"
      :aria-label="t('vocab.wordList.goto', { list: props.list.name })"
      @click="navigate()"
      @keydown.enter="navigate()"
    >
      <span class="word-list-container__name-container">
        <span class="word-list-container__name">{{ props.list.name }}</span>
        <span class="separator"></span>
      </span>
      <WordListContainerExamples
        v-if="lemmas.length"
        class="word-list-container__words"
        :words="lemmas"
      />

      <div class="word-list-container__button-container">
        <AbcBadge v-if="isNew">{{ t("vocab.wordList.newList") }}</AbcBadge>
        <div v-if="joinClassQuiz" class="word-list-container__button button-join-container">
          <span class="button-join-label">{{ t("vocab.wordList.classQuizInProgress") }}</span>
          <TgButton
            color="yellow"
            size="sm"
            tag="router-link"
            :to="{ name: 'vocab.classQuiz', params: { shareCode, quizCode } }"
            class="word-list-container__button"
            @click.stop="stopDoubleNavigation()"
          >
            {{ t("vocab.wordList.joinClassQuizButton") }}</TgButton
          >
        </div>
        <TgButton
          v-else-if="openClassQuiz"
          color="secondary"
          size="sm"
          tag="router-link"
          :to="{ name: 'vocab.classQuiz', params: { shareCode, quizCode } }"
          class="word-list-container__button"
          @click.stop="stopDoubleNavigation()"
          >{{ t("vocab.wordList.openClassQuizButton") }}</TgButton
        >
        <TgButton
          v-else-if="startClassQuiz"
          size="sm"
          class="word-list-container__button"
          @click.stop="createClassQuiz()"
          >{{ t("vocab.wordList.startClassQuizButton") }}</TgButton
        >
      </div>
    </div>
  </RouterLink>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.word-list-container {
  display: grid;
  grid-auto-rows: auto;
  grid-gap: 8px;
  grid-template-columns: 1fr auto;
  align-items: center;
  width: 100%;
  margin: 10px 0;
  padding: 17px 16px;
  border-radius: 8px;
  background: rgb(@colors[@white]);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  #media-breakpoint-up(sm, {
    grid-gap: 12px;
  });

  #media-breakpoint-up(md, {
    padding: 19px 26px;
    grid-template-columns: 32.5% auto minmax(130px, auto);
    grid-gap: 20px;
  });

  #media-breakpoint-up(lg, {
    grid-template-columns: 27.5% auto minmax(130px, auto);
  });

  #media-breakpoint-up(xl, {
    grid-template-columns: 22.5% auto minmax(130px, auto);
  });

  &__name-container {
    display: flex;
    grid-column: 1;
    grid-row: 1;
    justify-content: space-between;
  }

  &__name {
    overflow: hidden;
    color: rgb(@colors[@black]);
    font-size: 1.125rem;
    font-weight: bold;
    line-height: 1.75rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__words {
    grid-column: 1;
    grid-row: 2;
    overflow: hidden;

    #media-breakpoint-up(md, {
      grid-column: 2;
      grid-row: 1;
    });
  }

  &__button-container {
    display: flex;
    grid-column: 2;
    grid-row: span 2;
    justify-content: flex-end;

    #media-breakpoint-up(md, {
      grid-column: 3;
      grid-row: 1;
    });
  }
}

.separator {
  display: none;

  #media-breakpoint-up(md, {
    display: inline-block;
    border-left: 1px solid rgb(@theme-colors[ @grey][50]);
    line-height: 1.75rem;
  });
}

.button-join-container {
  display: flex;
  flex-direction: column;
  align-items: center;

  #media-breakpoint-up(xl, {
    flex-direction: row;
  });
}

.button-join-label {
  margin-bottom: 8px;
  color: rgb(@theme-colors[@grey][base]);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;

  #media-breakpoint-up(xl, {
    margin: 0px 12px 0 0;
    font-size: 1rem;
    line-height: 1.5rem;
  });
}
</style>
